// PortfolioLedger — v2 landing-after-source: the portfolio verdict over a
// flows × kinds ledger. THIS SCREEN IS THE SUITE RUN: entering it replays
// every scenario through the assertion engine (suiteRunAll), and every glyph
// is one scenario — shape = kind, color = the verdict that run actually earned.
//
// Truthfulness: the hero only says "this design holds" when every manifest
// scenario has a passing suite row. While running it reports progress; rows
// that were skipped surface with their reasons; scenarios that never ran
// render dim, not green.

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectManifest,
  selectActiveSource,
  selectWebSocketState,
} from '../../store/slices/scenarioSourceSlice.ts';
import {
  selectSuiteResults,
  selectSuiteRunning,
  selectCoverageSummary,
} from '../../store/slices/simulationSlice.ts';
import { suiteRunAll } from '../../store/sagas/suiteSaga.ts';
import { openFlowLedger } from '../../store/slices/uiSlice.ts';
import {
  classifyScenarioType,
  KIND_GLYPHS,
  KIND_NAMES,
  KIND_DEFINITIONS,
  VERDICT_GLYPH_COLORS,
  normalizeSuiteVerdict,
} from '../simulation/scenarioBadges.ts';
import {
  LEDGER_KIND_ORDER,
  LEDGER_VERDICT_LABELS,
  verdictMapFromSuite,
  tallyVerdicts,
  ledgerVerdict,
  sourceDisplayLabel,
} from './ledgerData.ts';
import { getCachedTopology } from '../../services/RemoteScenarioLoader.ts';
import type { RootState } from '../../store/index.ts';
import type { SimulationTopology } from '../../types/index.ts';

export default function PortfolioLedger() {
  const dispatch = useDispatch();
  const manifest = useSelector(selectManifest);
  const activeSource = useSelector(selectActiveSource);
  const wsState = useSelector(selectWebSocketState);
  const results = useSelector(selectSuiteResults);
  const running = useSelector(selectSuiteRunning);
  const coverage = useSelector(selectCoverageSummary);
  const userTopologies = useSelector(
    (s: RootState) => s.scenarioSource[s.scenarioSource.activeSource].loadedTopologies,
  );

  const [teachOpen, setTeachOpen] = useState(true);
  const [lastReplayAt, setLastReplayAt] = useState<string | null>(null);

  // The ledger IS the suite run — replay on first entry with this manifest.
  // Keyed by manifest IDENTITY (not a boolean) so switching to another
  // source's manifest — whose stale rows were cleared — replays for it too.
  const autoRanFor = useRef<object | null>(null);
  useEffect(() => {
    if (!manifest || running || autoRanFor.current === manifest) return;
    if (results.length === 0) {
      autoRanFor.current = manifest;
      dispatch(suiteRunAll());
    }
  }, [manifest, running, results.length, dispatch]);

  // Record when a run finishes (session-local truth — no fabricated times).
  // Render-time state adjustment (React's "derive state from prop change"
  // pattern) — avoids a cascading setState-in-effect.
  const [prevRunning, setPrevRunning] = useState(running);
  if (running !== prevRunning) {
    setPrevRunning(running);
    if (prevRunning && !running) {
      setLastReplayAt(
        new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
      );
    }
  }

  if (!manifest) {
    return (
      <div className="fv2-page" data-screen="portfolio">
        <div className="fv2-page-inner">
          <div className="fv2-kicker">PORTFOLIO VERDICT</div>
          <h1 className="fv2-ledger-title fv2-dim">No manifest loaded for this source yet.</h1>
          <p className="fv2-hero-copy">
            Choose a source from the landing screen, upload artifacts, or connect the live bridge.
          </p>
        </div>
      </div>
    );
  }

  const allEntries = manifest.flows.flatMap((f) => f.scenarios);
  const verdicts = verdictMapFromSuite(results);
  const tally = tallyVerdicts(allEntries, verdicts);
  const overall = ledgerVerdict(tally);
  const complete = !running && tally.notRun === 0 && tally.total > 0;
  const sourceLabel = sourceDisplayLabel(activeSource, wsState.specFile);

  // Portfolio-wide kind counts for the column headers.
  const kindCounts: Partial<Record<string, number>> = {};
  for (const e of allEntries) {
    const k = classifyScenarioType(e);
    kindCounts[k] = (kindCounts[k] ?? 0) + 1;
  }
  const kinds = LEDGER_KIND_ORDER.filter((k) => (kindCounts[k] ?? 0) > 0);

  const skippedRows = results.filter((r) => normalizeSuiteVerdict(r.verdict) === 'skipped');
  const ranCount = results.length - skippedRows.length;

  const topologyFor = (flowId: string): SimulationTopology | null =>
    userTopologies[flowId] ?? getCachedTopology(flowId);

  // Hero headline — earned, never assumed.
  let heroTop: React.ReactNode;
  let heroBottom: React.ReactNode = null;
  if (running) {
    heroTop = <>Replaying every scenario…</>;
    heroBottom = (
      <span className="fv2-dim">
        {results.length} of {tally.total} proofs recorded
      </span>
    );
  } else if (overall === 'pass' && complete) {
    heroTop = <>This design holds.</>;
    heroBottom = (
      <>
        <span style={{ color: 'var(--green)' }}>
          {tally.pass} of {tally.total}
        </span>{' '}
        proofs pass.
      </>
    );
  } else if (overall === null) {
    heroTop = <>Not yet proven.</>;
    heroBottom = <span className="fv2-dim">replay the suite to earn a verdict</span>;
  } else {
    heroTop = (
      <>
        <span style={{ color: overall === 'fail' ? 'var(--red)' : '#fb923c' }}>
          {tally.pass} of {tally.total}
        </span>{' '}
        proofs pass.
      </>
    );
    heroBottom = (
      <span style={{ fontSize: '.55em', color: 'var(--text-muted)' }}>
        {tally.fail > 0 && `${tally.fail} failed · `}
        {tally.error > 0 && `${tally.error} errored · `}
        {tally.skipped > 0 && `${tally.skipped} skipped · `}
        {tally.notRun > 0 && `${tally.notRun} never ran`}
      </span>
    );
  }

  return (
    <div className="fv2-page" data-screen="portfolio">
      <div className="fv2-page-inner fv2-portfolio-hero">
        <div className="fv2-kicker">
          PORTFOLIO VERDICT · {sourceLabel} · REPLAYED THROUGH THE ASSERTION ENGINE
        </div>
        <h1 className="fv2-ledger-title">
          {heroTop}
          {heroBottom && <br />}
          {heroBottom}
        </h1>
        <div className="fv2-portfolio-meta">
          <span>
            <b>{manifest.flows.length}</b> flows
          </span>
          <span>
            <b>{ranCount}</b> of <b>{tally.total}</b> ran ·{' '}
            <b>{skippedRows.length + tally.notRun}</b> skipped
          </span>
          <span>
            <b>{tally.fail}</b> failed · <b>{tally.error}</b> errors
          </span>
          {coverage && coverage.totalNodes > 0 && (
            <span>
              coverage{' '}
              <b>
                {coverage.coveredNodes}/{coverage.totalNodes}
              </b>{' '}
              nodes
              {coverage.totalBranches > 0 && (
                <>
                  {' '}
                  ·{' '}
                  <b>
                    {coverage.coveredBranches}/{coverage.totalBranches}
                  </b>{' '}
                  branches
                </>
              )}
            </span>
          )}
          {lastReplayAt && (
            <span>
              last replay <b>{lastReplayAt}</b>
            </span>
          )}
          <button
            className="fv2-replay-btn"
            onClick={() => dispatch(suiteRunAll())}
            disabled={running}
          >
            {running ? '⏳ REPLAYING…' : '▶ REPLAY ALL'}
          </button>
        </div>
        {overall === 'pass' && complete && (
          <div
            className="fv2-pass-stamp"
            aria-label={`PASS — ${tally.pass} of ${tally.total} scenarios`}
          >
            <div className="fv2-stamp-word">PASS</div>
            <div className="fv2-stamp-count">
              {tally.pass} / {tally.total} SCENARIOS
            </div>
            <div className="fv2-stamp-src">FACET ASSERTION ENGINE</div>
          </div>
        )}
      </div>

      {teachOpen && (
        <div className="fv2-page-inner fv2-teach-wrap">
          <div className="fv2-teach-card">
            <p>
              First time here? A <b>flow</b> is one end-to-end journey. Each flow is proven by{' '}
              <b>scenarios</b> — designed situations of a <b>kind</b>:{' '}
              <span className="fv2-mono" style={{ color: 'var(--blue)' }}>
                ○ happy
              </span>
              ,{' '}
              <span className="fv2-mono" style={{ color: 'var(--indigo)' }}>
                ◆ failure
              </span>
              ,{' '}
              <span className="fv2-mono" style={{ color: '#a78bfa' }}>
                ⊘ negative
              </span>
              ,{' '}
              <span className="fv2-mono" style={{ color: '#22d3ee' }}>
                ◔ timeout
              </span>
              . The kind says <b>what a scenario tests</b>; the verdict says{' '}
              <b>how the proof went</b>. A failure-path that{' '}
              <b style={{ color: 'var(--green)' }}>passes</b> is good news — the design fails
              exactly where it said it would.
            </p>
            <button className="fv2-teach-dismiss" onClick={() => setTeachOpen(false)}>
              GOT IT ✕
            </button>
          </div>
        </div>
      )}

      <div className="fv2-page-inner fv2-ledger-table-wrap">
        <div
          className="fv2-ledger-head"
          style={{ gridTemplateColumns: `minmax(200px,1.3fr) repeat(${kinds.length},1fr) 120px` }}
        >
          <div className="fv2-colhead">FLOW</div>
          {kinds.map((k) => (
            <div className="fv2-colhead" key={k}>
              <span style={{ color: 'var(--text)', marginRight: 6 }}>{KIND_GLYPHS[k]}</span>
              {KIND_NAMES[k]} <span className="fv2-dim">· {kindCounts[k]}</span>
              <span className="fv2-colhead-def">{KIND_DEFINITIONS[k].split(' — ')[0]}</span>
            </div>
          ))}
          <div className="fv2-colhead" style={{ textAlign: 'right' }}>
            VERDICT
          </div>
        </div>

        {manifest.flows.map((flow) => {
          const flowTally = tallyVerdicts(flow.scenarios, verdicts);
          const flowVerdict = ledgerVerdict(flowTally);
          const topo = topologyFor(flow.flowId);
          const meta = topo
            ? `${topo.lanes.length} lanes · ${topo.frames.length} moments · ${topo.frames.filter((f) => f.isBranch).length} branch points`
            : `${flow.scenarios.length} scenarios`;
          return (
            <button
              key={flow.flowId}
              className="fv2-ledger-row"
              style={{
                gridTemplateColumns: `minmax(200px,1.3fr) repeat(${kinds.length},1fr) 120px`,
              }}
              onClick={() => dispatch(openFlowLedger(flow.flowId))}
            >
              <div>
                <div className="fv2-flow-name">{flow.flowName}</div>
                <div className="fv2-flow-meta">{meta}</div>
              </div>
              {kinds.map((k) => {
                const entries = flow.scenarios.filter((e) => classifyScenarioType(e) === k);
                return (
                  <div className="fv2-glyph-cell" key={k}>
                    {entries.length === 0 && <span className="fv2-dim">·</span>}
                    {entries.map((e) => {
                      const v = verdicts[e.scenarioId] ?? null;
                      const color = v === null ? 'var(--text-dim)' : VERDICT_GLYPH_COLORS[v];
                      const vLabel = v === null ? 'never ran' : v;
                      return (
                        <span
                          key={e.scenarioId}
                          title={`${e.scenarioLabel} — ${vLabel}`}
                          style={{ color }}
                        >
                          {v === 'skipped' ? '—' : KIND_GLYPHS[k]}
                        </span>
                      );
                    })}
                  </div>
                );
              })}
              <div className="fv2-row-verdict">
                {flowVerdict === null ? (
                  <span className="fv2-dim">NOT RUN</span>
                ) : (
                  <span style={{ color: LEDGER_VERDICT_LABELS[flowVerdict].color }}>
                    {LEDGER_VERDICT_LABELS[flowVerdict].label}
                  </span>
                )}
                <span className="fv2-row-verdict-sub">
                  {flowTally.total - flowTally.skipped - flowTally.notRun} OF {flowTally.total} RAN
                  · {flowTally.skipped + flowTally.notRun} SKIPPED
                </span>
              </div>
            </button>
          );
        })}

        {skippedRows.length > 0 && (
          <div className="fv2-skip-list">
            <div className="fv2-colhead">SKIPPED — WITH REASONS (A SKIP IS NEVER SILENCE)</div>
            {skippedRows.map((r) => (
              <div key={r.scenarioId} className="fv2-skip-row">
                <span className="fv2-dim">—</span> {r.scenarioLabel}
                {r.reason && <span className="fv2-skip-reason"> — {r.reason}</span>}
              </div>
            ))}
          </div>
        )}

        <div className="fv2-ledger-legend">
          <span>
            EVERY GLYPH = ONE SCENARIO · <b>SHAPE = KIND · COLOR = VERDICT</b>
          </span>
          <span className="fv2-legend-rule" />
          <span style={{ color: 'var(--green)' }}>✓ PASS</span>
          <span style={{ color: 'var(--red)' }}>✗ FAIL</span>
          <span style={{ color: '#fb923c' }}>⚠ ERROR</span>
          <span>— SKIP (with reason)</span>
        </div>
        <p className="fv2-ledger-note">
          This ledger is the suite run — every scenario replays on load
          {activeSource === 'websocket' ? ', and on every save over the live bridge' : ''}.
        </p>
      </div>
    </div>
  );
}
